import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  MousePointer,
  Package,
  Target,
  ClipboardList,
  TestTube,
  FlaskConical,
  MessageCircle,
  Settings,
  Car,
  Wand2,
} from 'lucide-react';
import SimulationCard from '../../components/SimulationCard';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/subscription/SubscriptionContext';
import { LockedFeature } from '../../components/subscription/UpgradePrompt';

export type UseCaseType =
  | 'survey-and-focus-groups'
  | 'concept-test'
  | 'driving-concept-test'
  | 'vitamin-concept-test'
  | 'packaging-review-v2'
  | 'packaging-variant-comparison'
  | 'claims-testing'
  | 'attribution'
  | 'channel-strategy'
  | 'image-survey'
  | 'healthcare-chat-v2'
  | 'bluepill-chat'
  | 'hbs-chat'
  | 'test-use-case'
  | 'survey-creator'
  | 'survey-chat-154'
  | null;

interface UseCaseOption {
  label: string;
  type: UseCaseType;
  icon: React.ReactNode;
  description: string;
  redirect?: boolean;
}

interface CategoryConfig {
  title: string;
  useCases: UseCaseOption[];
}

interface LandingScreenProps {
  onSelectUseCase: (useCaseType: UseCaseType, useCaseTitle: string) => void;
}

interface GetCategoriesOptions {
  isAdmin: boolean;
  isPanasonicUser: boolean;
  isSmartypantsUser: boolean;
  isDemoUser: boolean;
  isKnfUser: boolean;
  isBacchusUser: boolean;
  isHbsUser: boolean;
  isWellnesspetUser: boolean;
  isMondelezUser: boolean;
  isArcherjerkyUser: boolean;
  isAmysUser: boolean;
}

const getCategories = ({ isAdmin, isPanasonicUser, isSmartypantsUser, isDemoUser, isKnfUser, isBacchusUser, isHbsUser, isWellnesspetUser, isMondelezUser, isArcherjerkyUser, isAmysUser }: GetCategoriesOptions): CategoryConfig[] => {
  // Determine which concept test to show (if any)
  // Panasonic users -> driving-concept-test as "Concept Test"
  // Admins -> all three (AI, driving, vitamin)
  // Demo, smartypants, bacchus, wellnesspet, mondelez, knf, archerjerky -> vitamin-concept-test as "Concept Test"
  const showVitaminConceptTest = !isPanasonicUser && (isAdmin || isDemoUser || isSmartypantsUser || isBacchusUser || isWellnesspetUser || isMondelezUser || isKnfUser || isArcherjerkyUser || isAmysUser);
  const showDrivingConceptTest = !isKnfUser && (isPanasonicUser || isAdmin);
  const showAiConceptTest = !isKnfUser && isAdmin;

  return [
    {
      title: 'Research',
      useCases: [
        {
          label: 'Surveys (Qual and Quant)',
          type: 'survey-and-focus-groups',
          icon: <ClipboardList className="w-5 h-5" />,
          description: 'Run qualitative and quantitative interviews to get deep insights',
        },
        {
          label: 'Focus Group',
          type: 'healthcare-chat-v2',
          icon: <MessageCircle className="w-5 h-5" />,
          description: 'Chat with an AI consumer-twin focus group',
          redirect: true,
        },
        // HBS Chat - only visible to HBS users (opens in new tab)
        ...(isHbsUser ? [{
          label: 'Focus Group',
          type: 'hbs-chat' as UseCaseType,
          icon: <MessageCircle className="w-5 h-5" />,
          description: 'Chat with an AI consumer-twin focus group',
          redirect: true,
        }] : []),
        // Survey Chat - visible to KNF, ArcherJerky, Amy's, and admin users
        ...((isKnfUser || isArcherjerkyUser || isAmysUser || isAdmin) ? [{
          label: 'Human Survey Insights',
          type: 'survey-chat-154' as UseCaseType,
          icon: <ClipboardList className="w-5 h-5" />,
          description: 'Chat with your human survey data and instantly surface key insights and themes.',
          redirect: true,
        }] : []),
        // Hidden for now — access via /chat-with-bluepill directly
        // {
        //   label: 'Chat with Bluepill',
        //   type: 'bluepill-chat',
        //   icon: <MessageCircle className="w-5 h-5" />,
        //   description: 'Chat with AI Consumer Twins powered by Bluepill',
        //   redirect: true,
        // },
        ...(isAdmin ? [{
          label: 'Survey Creation Agent',
          type: 'survey-creator' as UseCaseType,
          icon: <Wand2 className="w-5 h-5" />,
          description: 'Co-create a survey with an AI agent — draft, refine, and save in one flow',
          redirect: true,
        }] : []),
        ...(isAdmin ? [{
          label: 'Test Use Case',
          type: 'test-use-case' as UseCaseType,
          icon: <Settings className="w-5 h-5" />,
          description: 'Simulate a test to get insights',
        }] : []),
      ],
    },
    {
      title: 'Product & Innovation',
      useCases: [
        // Vitamin Concept Test - shown as "Concept Test" for admins, demo, smartypants
        ...(showVitaminConceptTest ? [{
          label: 'Concept Test',
          type: 'vitamin-concept-test' as UseCaseType,
          icon: <TestTube className="w-5 h-5" />,
          description: 'Test concepts with AI-simulated populations',
        }] : []),
        // Driving Concept Test - shown as "Concept Test" for Panasonic users only
        ...(showDrivingConceptTest ? [{
          label: 'Concept Test',
          type: 'driving-concept-test' as UseCaseType,
          icon: <Car className="w-5 h-5" />,
          description: 'Test concepts with AI-simulated populations',
        }] : []),
        // AI Concept Test - visible to admins (but not knf user)
        ...(showAiConceptTest ? [{
          label: 'AI Concept Test',
          type: 'concept-test' as UseCaseType,
          icon: <TestTube className="w-5 h-5" />,
          description: 'Test product concepts with AI-simulated consumers to predict purchase intent',
        }] : []),
        {
          label: 'Packaging Review',
          type: 'packaging-review-v2',
          icon: <Package className="w-5 h-5" />,
          description: 'Analyze a packaging design with shelf test, competitive comparison, and consumer reactions',
        },
        {
          label: 'Packaging Variant Comparison',
          type: 'packaging-variant-comparison',
          icon: <Package className="w-5 h-5" />,
          description: 'Compare 2-5 packaging variants to find which drives highest purchase intent',
        },
        {
          label: 'Claims Testing',
          type: 'claims-testing',
          icon: <FlaskConical className="w-5 h-5" />,
          description: 'Test and rank product claims using MaxDiff analysis to find what resonates most',
        },
      ],
    },
    {
      title: 'Marketing',
      useCases: [
        {
          label: 'AB Test Messaging',
          type: 'attribution',
          icon: <Target className="w-5 h-5" />,
          description: 'Learn what drives or blocks conversions',
        },
        {
          label: 'A/B Test or Review Creatives and Ads',
          type: 'channel-strategy',
          icon: <TrendingUp className="w-5 h-5" />,
          description: 'Instantly predict which creative will perform best, get insights on what resonates',
        },
        {
          label: 'Creative Rank Test',
          type: 'image-survey',
          icon: <MousePointer className="w-5 h-5" />,
          description: 'Run a ranked click test to identify the best creatives for your listing',
        },
      ],
    },
  ];
};

const DRIVING_ALLOWED_DOMAINS = (import.meta.env.VITE_DRIVING_ALLOWED_DOMAINS || '')
  .split(',')
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

const SMARTYPANTS_ALLOWED_DOMAINS = (import.meta.env.VITE_SMARTYPANTS_ALLOWED_DOMAINS || '')
  .split(',')
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

// Define restricted use cases for Demo users (demo@blue-pill.ai)
const DEMO_ALLOWED_USE_CASES: UseCaseType[] = [
  'survey-and-focus-groups',    // Surveys (Qual and Quant)
  'healthcare-chat-v2',         // Consumer Insights Chat
  'bluepill-chat',              // Chat with Bluepill
  'packaging-review-v2',        // Packaging Review
  'vitamin-concept-test',       // Concept Test
  'claims-testing',             // Claims Testing
];

// Define restricted use cases for Smartypants users
const SMARTYPANTS_ALLOWED_USE_CASES: UseCaseType[] = [
  'survey-and-focus-groups',    // Surveys (Qual and Quant)
  'healthcare-chat-v2',         // Consumer Insights Chat
  'bluepill-chat',              // Chat with Bluepill
  'packaging-review-v2',        // Packaging Review
  'vitamin-concept-test',       // Concept Test
  'claims-testing',             // Claims Testing
];

// Define restricted use cases for KNF users (bonebroths.com, kettleandfire.com, knf@blue-pill.ai)
const KNF_ALLOWED_USE_CASES: UseCaseType[] = [
  'survey-and-focus-groups',    // Surveys (Qual and Quant)
  'healthcare-chat-v2',         // Consumer Insights Chat
  'bluepill-chat',              // Chat with Bluepill
  'packaging-review-v2',        // Packaging Review
  'claims-testing',             // Claims Testing
  'attribution',                // AB Test Messaging
  'channel-strategy',           // A/B Test or Review Creatives and Ads
  'image-survey',               // Creative Rank Test
  'vitamin-concept-test',       // Concept Test (with KNF bone broth population)
  'survey-chat-154',            // Chat with Survey (survey ID 154)
];

// Define restricted use cases for Bacchus users
const BACCHUS_ALLOWED_USE_CASES: UseCaseType[] = [
  'survey-and-focus-groups',    // Surveys (Qual and Quant)
  'healthcare-chat-v2',         // Consumer Insights Chat
  'bluepill-chat',              // Chat with Bluepill
  'packaging-review-v2',        // Packaging Review
  'claims-testing',             // Claims Testing
  'vitamin-concept-test',       // Concept Test (uses cookie population)
];

const BACCHUS_ALLOWED_DOMAINS = (import.meta.env.VITE_BACCHUS_ALLOWED_DOMAINS || '')
  .split(',')
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

// Define restricted use cases for WellnessPet users
const WELLNESSPET_ALLOWED_USE_CASES: UseCaseType[] = [
  'survey-and-focus-groups',    // Surveys (Qual and Quant)
  'healthcare-chat-v2',         // Consumer Insights Chat
  'packaging-review-v2',        // Packaging Review
  'vitamin-concept-test',       // Concept Test
  'claims-testing',             // Claims Testing
];

const WELLNESSPET_ALLOWED_DOMAINS = (import.meta.env.VITE_WELLNESSPET_ALLOWED_DOMAINS || '')
  .split(',')
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

// Define restricted use cases for HBS users (mba2026.hbs.edu domain)
const HBS_ALLOWED_USE_CASES: UseCaseType[] = [
  'packaging-review-v2',        // Packaging Review
  'image-survey',               // Creative Rank Test
  'survey-and-focus-groups',    // Surveys (Qual and Quant)
  'hbs-chat',                   // Chat - opens in new tab
  'claims-testing',             // Claims Testing
];

// Define restricted use cases for AETV users (aegm.com domain)
const AETV_ALLOWED_USE_CASES: UseCaseType[] = [
  'survey-and-focus-groups',    // Surveys (Qual and Quant)
  'attribution',                // AB Test Messaging
  'image-survey',               // Creative Rank Test
  'claims-testing',             // Claims Testing
];

// Define restricted use cases for FlavCity users (marie@shopflavcity.com)
const FLAVCITY_ALLOWED_USE_CASES: UseCaseType[] = [
  'survey-and-focus-groups',    // Surveys (Qual and Quant)
  'healthcare-chat-v2',         // Consumer Insights Chat
  'claims-testing',             // Claims Testing
];

// Define restricted use cases for Malk users (malkorganics.com domain) — packaging only
const MALK_ALLOWED_USE_CASES: UseCaseType[] = [
  'packaging-review-v2',          // Packaging Review (individual)
  'packaging-variant-comparison', // Packaging Variant Comparison (variant)
];

// Define restricted use cases for Mondelez users (mohita@blue-pill.ai, sahil@blue-pill.ai)
const MONDELEZ_ALLOWED_USE_CASES: UseCaseType[] = [
  'survey-and-focus-groups',    // Surveys (Qual and Quant)
  'healthcare-chat-v2',         // Consumer Insights Chat
  'packaging-review-v2',        // Packaging Review
  'vitamin-concept-test',       // Concept Test (uses cookie population)
  'claims-testing',             // Claims Testing
];

// Define restricted use cases for Archerjerky users (archerjerky.com domain)
const ARCHERJERKY_ALLOWED_USE_CASES: UseCaseType[] = [
  'survey-and-focus-groups',    // Surveys (Qual and Quant)
  'healthcare-chat-v2',         // Consumer Insights Chat
  'packaging-review-v2',        // Packaging Review
  'vitamin-concept-test',       // Concept Test (uses jerky population)
  'claims-testing',             // Claims Testing
  'survey-chat-154',            // Human Survey Insights (survey 155 + group 24)
];

// Define restricted use cases for Amy's users (amys.com domain) — survey chat + frozen-population concept test
const AMYS_ALLOWED_USE_CASES: UseCaseType[] = [
  'survey-chat-154',            // Human Survey Insights (Amy's × Albertsons combined: 161 + 168)
  'vitamin-concept-test',       // Concept Test on the full population_amys_frozen (no segments)
];

const LandingScreen: React.FC<LandingScreenProps> = ({ onSelectUseCase }) => {
  const navigate = useNavigate();
  const { isAdmin, user, isDemoUser, isKnfUser, isBacchusUser, isHbsUser, isAetvUser, isFlavcityUser, isMalkUser, isWellnesspetUser, isMondelezUser, isArcherjerkyUser, isAmysUser } = useAuth();
  const { hasFeatureAccess, currentSubscription } = useSubscription();

  const userDomain = user?.email?.split('@')[1]?.toLowerCase() || '';

  // Check if user is from Panasonic domain (driving-only user)
  const isPanasonicUser = !!userDomain && DRIVING_ALLOWED_DOMAINS.includes(userDomain);

  // Check if user is from Smartypants domain
  const isSmartypantsUser = !!userDomain && SMARTYPANTS_ALLOWED_DOMAINS.includes(userDomain);

  const categories = getCategories({ isAdmin, isPanasonicUser, isSmartypantsUser, isDemoUser, isKnfUser, isBacchusUser, isHbsUser, isWellnesspetUser, isMondelezUser, isArcherjerkyUser, isAmysUser });

  const handleUseCaseClick = (useCase: UseCaseOption) => {
    if (useCase.redirect) {
      if (useCase.type === 'healthcare-chat-v2') {
        navigate('/ask-natural-organic');
        return;
      }
      if (useCase.type === 'bluepill-chat') {
        navigate('/chat-with-bluepill');
        return;
      }
      if (useCase.type === 'hbs-chat') {
        window.open('/ask-natural-organic', '_blank');
        return;
      }
      if (useCase.type === 'survey-creator') {
        navigate('/survey-creator');
        return;
      }
      if (useCase.type === 'survey-chat-154') {
        // Landing entry point = the client's standalone survey; the in-chat switcher then
        // lets them change surveys. Must match SURVEY_CHAT_CLIENTS in backend survey_chat.py.
        const entrySurveyId = isArcherjerkyUser ? 155
          : (isAmysUser || user?.email === 'puneet@blue-pill.ai') ? 161
          : 154;
        navigate(`/surveys/${entrySurveyId}/chat?from=landing`);
        return;
      }
    }

    onSelectUseCase(useCase.type, useCase.label);
  };

  // Check if a use case should be disabled for Panasonic users
  const isDisabledForPanasonic = (useCaseType: UseCaseType) => {
    if (!isPanasonicUser) return false;
    // For Panasonic users, only driving-concept-test is enabled
    return useCaseType !== 'driving-concept-test';
  };

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Start a new study.
        </h1>
        <p className="text-gray-500">
          Run research, test ideas, and evaluate marketing with your AI audience.
        </p>
      </div>

      {(() => {
        // Calculate visible categories for dynamic grid sizing
        const visibleCategories = categories.filter((category) => {
          // For HBS users, only show categories that have at least one allowed use case
          if (isHbsUser && !isAdmin) {
            return category.useCases.some((uc) => HBS_ALLOWED_USE_CASES.includes(uc.type));
          }
          // For AETV users, only show categories that have at least one allowed use case
          if (isAetvUser && !isAdmin) {
            return category.useCases.some((uc) => AETV_ALLOWED_USE_CASES.includes(uc.type));
          }
          // For FlavCity users, only show categories that have at least one allowed use case
          if (isFlavcityUser && !isAdmin) {
            return category.useCases.some((uc) => FLAVCITY_ALLOWED_USE_CASES.includes(uc.type));
          }
          // For Malk users, only show categories that have at least one allowed use case
          if (isMalkUser && !isAdmin) {
            return category.useCases.some((uc) => MALK_ALLOWED_USE_CASES.includes(uc.type));
          }
          // For KNF users, only show categories that have at least one allowed use case
          if (isKnfUser && !isAdmin) {
            return category.useCases.some((uc) => KNF_ALLOWED_USE_CASES.includes(uc.type));
          }
          // For demo users, only show categories that have at least one allowed use case
          if (isDemoUser && !isAdmin) {
            return category.useCases.some((uc) => DEMO_ALLOWED_USE_CASES.includes(uc.type));
          }
          // For Smartypants users, only show categories that have at least one allowed use case
          if (isSmartypantsUser && !isAdmin) {
            return category.useCases.some((uc) => SMARTYPANTS_ALLOWED_USE_CASES.includes(uc.type));
          }
          // For Bacchus users, only show categories that have at least one allowed use case
          if (isBacchusUser && !isAdmin) {
            return category.useCases.some((uc) => BACCHUS_ALLOWED_USE_CASES.includes(uc.type));
          }
          // For WellnessPet users, only show categories that have at least one allowed use case
          if (isWellnesspetUser && !isAdmin) {
            return category.useCases.some((uc) => WELLNESSPET_ALLOWED_USE_CASES.includes(uc.type));
          }
          // For Mondelez users, only show categories that have at least one allowed use case
          if (isMondelezUser && !isAdmin) {
            return category.useCases.some((uc) => MONDELEZ_ALLOWED_USE_CASES.includes(uc.type));
          }
          // For Archerjerky users, only show categories that have at least one allowed use case
          if (isArcherjerkyUser && !isAdmin) {
            return category.useCases.some((uc) => ARCHERJERKY_ALLOWED_USE_CASES.includes(uc.type));
          }
          // For Amy's users, only show categories that have at least one allowed use case
          if (isAmysUser && !isAdmin) {
            return category.useCases.some((uc) => AMYS_ALLOWED_USE_CASES.includes(uc.type));
          }
          return true;
        });

        // Dynamic grid classes based on visible category count
        const gridClasses = visibleCategories.length === 2
          ? "grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          : visibleCategories.length === 1
            ? "grid grid-cols-1 gap-8 max-w-md mx-auto"
            : "grid grid-cols-1 md:grid-cols-3 gap-8";

        return (
          <div className={gridClasses}>
            {visibleCategories.map((category) => (
          <div key={category.title}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {category.title}
            </h3>
            <div className="flex flex-col gap-4">
              {category.useCases.map((useCase) => {
                // Filter use cases for HBS users
                if (isHbsUser && !isAdmin && !HBS_ALLOWED_USE_CASES.includes(useCase.type)) return null;
                // Filter use cases for AETV users
                if (isAetvUser && !isAdmin && !AETV_ALLOWED_USE_CASES.includes(useCase.type)) return null;
                // Filter use cases for FlavCity users
                if (isFlavcityUser && !isAdmin && !FLAVCITY_ALLOWED_USE_CASES.includes(useCase.type)) return null;
                // Filter use cases for Malk users
                if (isMalkUser && !isAdmin && !MALK_ALLOWED_USE_CASES.includes(useCase.type)) return null;
                // Filter use cases for KNF users
                if (isKnfUser && !isAdmin && !KNF_ALLOWED_USE_CASES.includes(useCase.type)) return null;
                // Filter use cases for demo users
                if (isDemoUser && !isAdmin && !DEMO_ALLOWED_USE_CASES.includes(useCase.type)) return null;
                // Filter use cases for Smartypants users
                if (isSmartypantsUser && !isAdmin && !SMARTYPANTS_ALLOWED_USE_CASES.includes(useCase.type)) return null;
                // Filter use cases for Bacchus users
                if (isBacchusUser && !isAdmin && !BACCHUS_ALLOWED_USE_CASES.includes(useCase.type)) return null;
                // Filter use cases for WellnessPet users
                if (isWellnesspetUser && !isAdmin && !WELLNESSPET_ALLOWED_USE_CASES.includes(useCase.type)) return null;
                // Filter use cases for Mondelez users
                if (isMondelezUser && !isAdmin && !MONDELEZ_ALLOWED_USE_CASES.includes(useCase.type)) return null;
                // Filter use cases for Archerjerky users
                if (isArcherjerkyUser && !isAdmin && !ARCHERJERKY_ALLOWED_USE_CASES.includes(useCase.type)) return null;
                // Filter use cases for Amy's users
                if (isAmysUser && !isAdmin && !AMYS_ALLOWED_USE_CASES.includes(useCase.type)) return null;

                const isChatRedirect = useCase.redirect;
                // Panasonic users always have access to driving-concept-test
                const isPanasonicDrivingAccess = isPanasonicUser && useCase.type === 'driving-concept-test';
                // Smartypants users always have access to their allowed use cases
                const isSmartypantsAccess = isSmartypantsUser && SMARTYPANTS_ALLOWED_USE_CASES.includes(useCase.type);
                // Demo users always have access to their allowed use cases
                const isDemoAccess = isDemoUser && DEMO_ALLOWED_USE_CASES.includes(useCase.type);
                // KNF users always have access to their allowed use cases
                const isKnfAccess = isKnfUser && KNF_ALLOWED_USE_CASES.includes(useCase.type);
                // Bacchus users always have access to their allowed use cases
                const isBacchusAccess = isBacchusUser && BACCHUS_ALLOWED_USE_CASES.includes(useCase.type);
                // WellnessPet users always have access to their allowed use cases
                const isWellnesspetAccess = isWellnesspetUser && WELLNESSPET_ALLOWED_USE_CASES.includes(useCase.type);
                // Mondelez users always have access to their allowed use cases
                const isMondelezAccess = isMondelezUser && MONDELEZ_ALLOWED_USE_CASES.includes(useCase.type);
                // Archerjerky users always have access to their allowed use cases
                const isArcherjerkyAccess = isArcherjerkyUser && ARCHERJERKY_ALLOWED_USE_CASES.includes(useCase.type);
                // Amy's users always have access to their allowed use cases
                const isAmysAccess = isAmysUser && AMYS_ALLOWED_USE_CASES.includes(useCase.type);
                // HBS Chat is always accessible for HBS users (no subscription check needed)
                const isHbsChatForHbsUser = useCase.type === 'hbs-chat' && isHbsUser;
                // HBS users always have access to their allowed use cases
                const isHbsAccess = isHbsUser && HBS_ALLOWED_USE_CASES.includes(useCase.type);
                // AETV users always have access to their allowed use cases
                const isAetvAccess = isAetvUser && AETV_ALLOWED_USE_CASES.includes(useCase.type);
                // FlavCity users always have access to their allowed use cases
                const isFlavcityAccess = isFlavcityUser && FLAVCITY_ALLOWED_USE_CASES.includes(useCase.type);
                // Malk users always have access to their allowed use cases
                const isMalkAccess = isMalkUser && MALK_ALLOWED_USE_CASES.includes(useCase.type);
                const hasAccess = isAdmin || isChatRedirect || isHbsChatForHbsUser || isPanasonicDrivingAccess || isSmartypantsAccess || isDemoAccess || isKnfAccess || isBacchusAccess || isWellnesspetAccess || isMondelezAccess || isArcherjerkyAccess || isAmysAccess || isHbsAccess || isAetvAccess || isFlavcityAccess || isMalkAccess || hasFeatureAccess(useCase.type as string);
                const isLocked = !hasAccess;
                const isPanasonicDisabled = isDisabledForPanasonic(useCase.type);

                // For Panasonic users: gray out all except driving-concept-test
                if (isPanasonicDisabled) {
                  return (
                    <SimulationCard
                      key={useCase.type}
                      icon={useCase.icon}
                      title={useCase.label}
                      description={useCase.description}
                      disabled
                      onClick={() => {}}
                    />
                  );
                }

                if (isLocked) {
                  return (
                    <LockedFeature
                      key={useCase.type}
                      feature={useCase.label}
                      requiredPlan={currentSubscription?.id === 'free' ? 'Starter' : 'Higher tier'}
                    >
                      <SimulationCard
                        icon={useCase.icon}
                        title={useCase.label}
                        description={useCase.description}
                        disabled
                        onClick={() => {}}
                      />
                    </LockedFeature>
                  );
                }

                return (
                  <SimulationCard
                    key={useCase.type}
                    icon={useCase.icon}
                    title={useCase.label}
                    description={useCase.description}
                    onClick={() => handleUseCaseClick(useCase)}
                  />
                );
              })}
            </div>
          </div>
        ))}
          </div>
        );
      })()}
    </div>
  );
};

export default LandingScreen;
